import { getPlatformInfo } from '@/utils/deviceUtils';
import { createSha256 } from '@/utils/hash';
import { sanitizeData } from '@riseupil/common-utils';
import _ from 'lodash';
import anonymousSession from './anonymousSession';
import config from './config';
import DDLogs from './DDLogs';

let incognito = false;

const SENSITIVE_PARAM = 'inviteToken';

const SIGNED_UP_EVENT_NAME = 'Signed Up';
// This event is a reserved event in segment, meant to be sent once upon use signup to enable matching between anonymousId and userId in segment

function _getPlatformInfo() {
  const platformInfo = getPlatformInfo();
  if (platformInfo.isNative) {
    return {
      platform: 'app',
      device: platformInfo.isIOS ? 'ios' : 'android',
      appVersion: platformInfo.appVersion,
      appAnonymousId: anonymousSession.getId(),
    };
  }

  return {
    platform: 'web',
    device: platformInfo.isIOS ? 'ios' : 'android',
  };
}

function _getSegmentSourceId() {
  if (window.location.search.includes(SENSITIVE_PARAM)) {
    return config.get().segmentDisabledSourceId;
  }
  return config.get().segmentSourceId;
}

/* eslint-disable */
const Segment = {
  install(Vue, options) {
    (function () {
      var analytics = window.analytics = window.analytics || [];
      if (!analytics.initialize) {
        if (analytics.invoked) {
          window.console && console.error && console.error('Segment snippet included twice.');
        } else {
          analytics.invoked = !0;
          analytics.methods =
            ['register', 'trackSubmit', 'trackClick', 'trackLink', 'trackForm', 'pageview', 'identify', 'reset', 'group', 'track', 'ready', 'alias', 'debug',
              'page', 'once', 'off', 'on', 'addSourceMiddleware', 'addIntegrationMiddleware', 'setAnonymousId', 'addDestinationMiddleware'];
          analytics.factory = function (e) {
            return function () {
              var t = Array.prototype.slice.call(arguments);
              t.unshift(e);
              analytics.push(t);
              return analytics;
            };
          };
          for (var e = 0; e < analytics.methods.length; e++) {
            var key = analytics.methods[e];
            analytics[key] = analytics.factory(key);
          }
          analytics.load = function (key, e) {
            var t = document.createElement('script');
            t.type = 'text/javascript';
            t.async = !0;
            t.src = 'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js';
            var n = document.getElementsByTagName('script')[0];
            n.parentNode.insertBefore(t, n);
            analytics._loadOptions = e;
          };

          analytics._writeKey = _getSegmentSourceId();
          analytics.SNIPPET_VERSION = '4.15.3';
          //@ts-ignore
          analytics.load(_getSegmentSourceId());
          //@ts-ignore
          analytics.page();
          analytics.register({
            name: 'Cookie Compatibility',
            version: '0.1.0',
            type: 'utility',
            load: (_ctx, ajs) => {
              const user = ajs.user()

              // stringify any existing IDs
              user.anonymousId(anonymousSession.getId())
              user.id(user.id())
            },
            isLoaded: () => true
          })
        }
      }
    })();
  },

  setIncognito(isIncognito) {
    incognito = isIncognito;
  },

  identifyAnonymousUser(data) {
    const sanitizedData = sanitizeData(DDLogs, data, 'identifyAnonymousUser');
    analytics.identify(anonymousSession.getId(), sanitizedData);
    DDLogs.log('identify', sanitizedData);
  },

  identifyCustomer(customerId, data) {
    const sanitizedData = sanitizeData(DDLogs, data, 'identifyCustomer');
    analytics.identify(
      customerId,
      _.assign({ incognito }, sanitizedData),
      {
        integrations: {
          'Facebook Pixel': false
        }
      });
    DDLogs.log('identify', _.assign({}, { customerId }, sanitizedData));
  },

  trackUserSignedUp(eventPrefix, customerId) {
    const eventName = `${eventPrefix}${SIGNED_UP_EVENT_NAME}`;
    const platformInfo = _getPlatformInfo();
    analytics.track(eventName, _.assign({ userId: customerId }, platformInfo));
    DDLogs.log(eventName, _.assign({ customerId }, platformInfo));
  },

  trackUserInteraction(eventName, data) {
    const sanitizedData = sanitizeData(DDLogs, data, eventName);
    const platformInfo = _getPlatformInfo();
    analytics.track(eventName, _.assign({ incognito }, sanitizedData, platformInfo, {
        eventType: 'interaction',
      }),
      {
        integrations: {
          'Facebook Pixel': false
        }
      });
    DDLogs.log(eventName, _.assign({}, sanitizedData, platformInfo));
  },

  trackUserInteractionToPixel(eventName, data) {
    const sanitizedData = sanitizeData(DDLogs, data, eventName);
    const platformInfo = _getPlatformInfo();
    analytics.track(eventName, _.assign({ incognito }, sanitizedData, platformInfo, {
        eventType: 'interaction',
      }),
      {
        integrations: {
          'Facebook Pixel': true
        }
      });
    DDLogs.log(eventName, _.assign({}, sanitizedData, platformInfo));
  },

  trackUserGot(eventName, data) {
    const sanitizedData = sanitizeData(DDLogs, data, eventName);
    const platformInfo = _getPlatformInfo();
    analytics.track(eventName, _.assign({ incognito }, sanitizedData, platformInfo, {
        eventType: 'push',
      }),
      {
        integrations: {
          'Facebook Pixel': false
        }
      });
    DDLogs.log(eventName, _.assign({}, sanitizedData, platformInfo));
  },
  trackUserGotToPixel(eventName, data, traits = {}) {
    const sanitizedData = sanitizeData(DDLogs, data, eventName);
    const platformInfo = _getPlatformInfo();
    analytics.track(eventName, _.assign({ incognito }, sanitizedData, platformInfo, {
        eventType: 'push',
      }),
      {
        integrations: {
          'Facebook Pixel': true
        }
      },
      {
       traits: _hashTraits(traits),
      });
    DDLogs.log(eventName, _.assign({}, sanitizedData, platformInfo));
  },

  reload() {
    analytics.load?.(_getSegmentSourceId());
  }
};

function _hashTraits(traits) {
  if (_.isEmpty(traits)) {
    return traits;
  }
  const traitsToHash = ['email', 'phone', 'name', 'firstName', 'lastName']
  return _.chain(traits)
    .toPairs()
    .map(([key, value]) => [key, traitsToHash.includes(key) ? createSha256(value) : value])
    .fromPairs()
    .value();
}

export default Segment;
